import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {
  ConsumeRequestData,
  GetPaymentParams,
  OperationType,
  PassportType,
  Request,
  RequestState,
  RequestWithDuplicata
} from '../models/request';
import {isJsonParsable, parseMetadata} from '../utils/parseMetadata';
import {RequestService} from './request.service';
import {documentTypeIsVisaExtAB, documentTypeIsVisaExtC, setCurrentCountryCat} from '../models/default-filter-strategy';
import {endOfDay} from 'date-fns';
import {PlatformEvent, PlatformEventType} from '../models/information';
import {Place} from '../models/place';
import {generateQrOffline} from '../utils/generateQr';
import {QrService} from './qr.service';
import {StorageService} from './storage.service';
import {Store} from '../models/store';
import {Subject} from 'rxjs';
import {PlacesService} from './places.service';
import {LoaderService, NavigateService} from 'ngx-satoris';
import {isEmptyObject} from '../utils/object';
import {Person} from '../models/person';
import {CartService} from './cart.service';
import {HttpErrorResponse} from '@angular/common/http';

const toCamelCase = (str: string) => str.charAt(0).toLowerCase() + str.slice(1);

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  public current$: Subject<RequestWithDuplicata> = new Subject();

  constructor(private api: ApiService,
              private request: RequestService,
              private qr: QrService,
              private loader: LoaderService,
              private places: PlacesService,
              private storage: StorageService,
              private cart: CartService,
              private nav: NavigateService) {}

  getPayment(id: string, loader = false, callParams: GetPaymentParams = {history: true, watchlist: true, apipnr: true, inbound: true, fullMetadata: true}, setQR = true): void {
    let savedFound: RequestWithDuplicata = undefined;
    let savedCallParamsDiff = false;
    this.storage.getFromStorage(Store.PAYMENT_STORE, id).then((res: RequestWithDuplicata) => {
      if(res) {
        savedFound = res;
        if(!savedFound.qrCode && savedFound.externalId) {
          const qrPlaceId = this.api.userRole?.isWorker ? this.api.userPlaceId : savedFound.timeslot_place_id;
          savedFound.qrCode = generateQrOffline(savedFound.id, qrPlaceId, savedFound.externalId);
        }
        savedFound.allProcessed = false;
        this.current$.next(savedFound);
      }
    }).finally(() => {
      let current: RequestWithDuplicata = undefined;
      this.api.status = 'info.readingPayment';

      if(loader) this.loader.loading(true);
      this.api.payment(id, callParams.history, callParams.watchlist, callParams.apipnr, callParams.inbound, false, savedFound?.lastModified || undefined).then(res => {
        current = res;
        current.savedCallParams = callParams;

        if(!savedFound?.savedCallParams || this.hasPropertyChangedToTrue(savedFound.savedCallParams, callParams)) savedCallParamsDiff = true;
        if(!!savedFound && !savedCallParamsDiff && this.hasNotBeenModified(savedFound, current)) {
          savedFound.allProcessed = true;
          this.save(savedFound, true);
          return;
        } else {
          current.allProcessed = false;
        }

        this.processOfflineData(current, setQR);

        if(loader) this.loader.loading(false);
        this.save(current, !savedFound);

        Promise.all([
          this.places.getPlace(current.created_place_id),
          this.places.getPlace(current.consumed_place_id),
          this.getConsumeData(current, callParams.fullMetadata),
          this.getSerializedData(current, callParams.fullMetadata),
          current.parent && this.getParentData(current)
        ]).then(([createdPlace, consumedPlace, consumeData, serializedData, parentData]: [Place, Place, ConsumeRequestData, any, RequestWithDuplicata | undefined]) => {
          current.createdPlace = createdPlace;
          current.consumedPlace = consumedPlace;
          current.consumeData = consumeData;
          current.metadata = serializedData;
          this.computePrice(current);
          if(parentData) {
            current.parent = parentData;
            current.moveEvents = parentData.moveEvents;
            this.save(current.parent, false);
          }
        }).finally(() => {
          this.processEvents(current).finally(() => {
            if(current.defaultLocalUsageUntilFromServer) current.isDeclaration = true;
            if(current.metadata) {
              current.canBeDeported =  this.api.env.nationalityNoDeclaration.includes(current.metadata.Nationality) || current.metadata.PassportType === PassportType.REFUGEE;
            }
          }).finally(() => {
            this.setCurrentBatchViews(current);
            current.allProcessed = true;
            this.save(current, true);
          });
        });
      }).catch(errors => {
        if(errors instanceof HttpErrorResponse && errors.status === 404) {
          this.loader.loading(true, {type: 'warn', message: 'err.payment.notAvailable'});
        } else {
          this.loader.loading(true, {type: 'error', message: errors});
        }
        this.nav.to('/dashboard');
      });
    }).catch(err => {
      this.loader.loading(true, {type: 'error', message: err});
    }).finally(() => {
      this.api.status = undefined;
    });
  }

  processOfflineData(current: RequestWithDuplicata, setQR = true) {
    const today = endOfDay(new Date());
    const usageAfter = endOfDay(new Date(current.usageAfter));
    const usageUntil = endOfDay(new Date(current.usageUntil));
    const localUsageUntil = endOfDay(new Date(current.localUsageUntil));

    current.document = this.api.userInfo.server.paymentRequests.find(d => d.id === current.operationId);
    // Removed sale from this type of doc...
    if(!current.document) {
      current.indexed = {};
      return;
    }
    current.indexed = {};
    if(current.internalIndexedData) {
      const parts = current.internalIndexedData.split('|');
      current.document.indexedFields.forEach((field, i) => current.indexed[field] = parts[i]);
    }
    if(current.parentInternalIndexedData) { // Fill empty indexedFields with parent data if available
      const parentDocument = this.api.userInfo.server.paymentRequests.find(d => d.id === current.document.parentType);
      if(parentDocument) {
        const parentParts = current.parentInternalIndexedData.split('|');
        parentDocument.indexedFields.forEach((field, i) => {
          if(!current.indexed[field] && field !== 'Urgent') {
            current.indexed[field] = parentParts[i];
          }
        });
      }
    }
    const parsedMetadata = isJsonParsable(current.metadata);
    if(current.internalIndexedData && (!current.metadata || (parsedMetadata && isEmptyObject(parsedMetadata)))) {
      current.metadata = {...current.indexed};
      Object.keys(current.indexed).filter(k => k.indexOf('.') > -1).forEach(k => {
        const parts = k.split('.');
        current.metadata[parts[0]] = current.metadata[parts[0]] || {};
        current.metadata[parts[0]][parts[1]] = current.indexed[k];
      });
    }

    current.defaultLocalUsageUntilFromServer = this.request.calculateLocalUntil(current.operationId, current.metadata.PassportType, current.metadata.Nationality, current.metadata.Documents?.PurposeOfVisit);
    current.localUsageUntilFromServer = this.request.calculateMaxLocalUntil(current.operationId, current.metadata.PassportType, current.metadata.Nationality, current.metadata.Documents?.PurposeOfVisit);

    current.events?.forEach(event => {
      event.indexed = {};
      if(event.internalIndexedData) {
        const parts = event.internalIndexedData.split('|');
        [...current.document.indexedFields].concat(...current.document.usageIndexFields).forEach((field, i) => event.indexed[field] = parts[i]);
      }
    });

    const qrPlaceId = this.api.userRole?.isWorker ? this.api.userPlaceId : current.timeslot_place_id;
    current.qrCode = generateQrOffline(current.id, qrPlaceId, current.externalId);
    current.checkoutIsNext = this.request.isNextCheckout(current);
    current.actualManualConfirmRounds = this.getManualConfirmRounds(current);
    current.isDiplomatic = current.indexed.PassportType === PassportType.DIPLOMATIC;
    current.isBlacklisted = this.isBlacklisted(current);
    current.isExtension = this.isExtension(current);
    current.usageAfterValid = today >= usageAfter;
    current.usageUntilValid = today <= usageUntil;
    current.localUsageUntilValid = current.localUsageUntil ? today <= localUsageUntil : true;
    current.isRevisionSinceOneWeek = new Date().getTime() - new Date(current.createdAt).getTime() > 604800000;
    current.isUsage = this.isUsage(current);
    current.cancelledEvents = this.getCancelledEvents(current);
    current.moveEvents = this.getMoveEvents(current);
    current.eventsNotice = this.getEventsNotices(current);
    current.eventsNotes = this.getEventsNotes(current);
    current.usageCount = this.calculateUsageCount(current);
    current.assignedUserId = current.assigned_user__id;
    current.eventsExtensions = this.getEventsExtensions(current);
    this.computePrice(current);
    if(current.metadata.Nationality || current.metadata.PassportType) {
      current.canBeDeported = this.api.env.nationalityNoDeclaration.includes(current.metadata.Nationality) || current.metadata.PassportType === PassportType.REFUGEE;
    }

    setCurrentCountryCat(current);

    if(setQR) {
      this.qr.qrCode = current.qrCode;
      this.qr.setCurrentQrData(undefined, current);
    }
  }

  private computePrice(current: RequestWithDuplicata) {
    if(!this.isExtension(current)) {
      current.amountCents = [RequestState.PROPOSED, RequestState.DELAY_PAY_NOT_CONFIRMED, RequestState.DELAY_PAY].includes(current.state) ? (current.document.amountCentsDyn ?
        eval(current.document.amountCentsDyn)(current.metadata, this.api.userPlace) : current.document.amountCents) : current.amountCents;
    }
  }

  getCancelledEvent(req: RequestWithDuplicata, eventId: number): PlatformEvent {
    if(req.cancelledEvents) {
      return req.cancelledEvents.find(e => +e.id === +eventId) || null;
    } else {
      return this.getCancelledEvents(req)?.find(e => +e.id === +eventId) || null;
    }
  }

  hasPropertyChangedToTrue(from: GetPaymentParams, to: GetPaymentParams): boolean {
    return Object.keys(from).some((key) => from[key as keyof GetPaymentParams] === false && to[key as keyof GetPaymentParams] === true);
  }

  private save(current: RequestWithDuplicata, next: boolean): void {
    this.storage.saveToStorage(Store.PAYMENT_STORE, current).catch(error => {
      console.error(`Error saving ${current.id}:`, error);
    });

    if(next) {
      this.current$.next(current);
    }
  }

  private getCancelledEvents(req: RequestWithDuplicata | Request): PlatformEvent[] {
    return req.events?.filter(event => event.type === PlatformEventType.PAYMENT_VOIDED).map(voidEvent => {
      const voidedEventId = (voidEvent?.context) ? isJsonParsable(voidEvent.context)?.platform_event_id : null;
      const usageEvent = voidedEventId ? req.events.find(e => e.id === voidedEventId && e.type === PlatformEventType.PAYMENT_USAGE_VOIDED) : null;
      if(usageEvent) {
        return {
          ...usageEvent,
          cancellationReason: isJsonParsable(voidEvent.context).reason
        };
      }
      return null;
    }).filter(event => event !== null);
  }

  private getSerializedData(req: RequestWithDuplicata, fullMetadata: boolean): Promise<any> {
    return this.api.getMetadataPayment(req.serialized || req.consumeData, undefined, fullMetadata).catch(() => undefined).then((res: any) => res || parseMetadata(req.metadata).metadata || parseMetadata(req.metadata));
  }

  private getConsumeData(req: RequestWithDuplicata, fullMetadata: boolean): Promise<ConsumeRequestData | undefined> {
    if(req.moveEvents.length) {
      for(const event of req.moveEvents) {
        const consumeDataUrl = JSON.parse(event.context as string).consumeData;
        if(req.consumeData === consumeDataUrl) {
          return this.api.getMetadataPayment(consumeDataUrl, undefined, fullMetadata) as Promise<ConsumeRequestData>;
        }
      }
    }
    return Promise.resolve(undefined);
  }

  private getMoveEvents(req: RequestWithDuplicata): PlatformEvent[] {
    return req.events?.filter((e: PlatformEvent) => (e.type === PlatformEventType.PAYMENT_USED || e.type === PlatformEventType.PAYMENT_USAGE_VOIDED) && e.consumeData?.Operation !== OperationType.REFUSEIN && e.consumeData?.Operation !== OperationType.REFUSEOUT).sort((a, b) => a.updatedOn.localeCompare(b.updatedOn));
  }

  private getEventsNotices(req: RequestWithDuplicata): PlatformEvent[] {
    return (req?.events ?? [])
      .filter((e: PlatformEvent) => e?.type === PlatformEventType.PAYMENT_USED && e.internalIndexedData.split('|').includes(OperationType.NOTICES_ACCEPTED))
      .sort((a, b) => a.updatedOn.localeCompare(b.updatedOn));
  }

  private getEventsNotes(req: RequestWithDuplicata): PlatformEvent[] {
    return (req?.events ?? [])
      .filter((e: PlatformEvent) => e?.type === PlatformEventType.PAYMENT_NOTE)
      .sort((a, b) => a.updatedOn.localeCompare(b.updatedOn));
  }

  private getEventsExtensions(req: RequestWithDuplicata): PlatformEvent[] {
    return (req?.events ?? [])
      .filter((e: PlatformEvent) => e?.type === PlatformEventType.PAYMENT_EXTENDED)
      .sort((a, b) => a.updatedOn.localeCompare(b.updatedOn));
  }

  private getManualConfirmRounds(req: RequestWithDuplicata | Request) {
    return this.api.userInfo.server.paymentRequests.find(document => document.id === req.operationId)?.manualConfirmRounds;
  }

  private isExtension(req: RequestWithDuplicata | Request) {
    return documentTypeIsVisaExtAB(req.operationId) || documentTypeIsVisaExtC(req.operationId);
  }

  private isBlacklisted(req: RequestWithDuplicata) {
    return this.request.isBlacklisted(req) && req.state === RequestState.EXPIRED;
  }

  private setCurrentBatchViews(current: RequestWithDuplicata) {
    current.batch.views.forEach(view => {
      view.document = this.api.userInfo.server.paymentRequests.find(d => d.id === view.operationId);
      view.indexed = {};
      const parts = view.internalIndexedData.split('|');
      view.document.indexedFields.forEach((field, i) => view.indexed[field] = parts[i]);
      (<any>view).displayName = view.indexed.FirstName + ' ' + view.indexed.LastName + ' | ' + view.indexed.PassportNumber;
    });
  }

  private processEvents(request: RequestWithDuplicata, next = true): Promise<void> {
    if(request.moveEvents.length) {
      const allPromises = request.moveEvents.map((event: PlatformEvent, index) => {
        const context: any = isJsonParsable(event.context);
        const eventPromises: Promise<void>[] = [];

        if(context && context.consumeData) {
          const metadataPromise = this.api.getMetadataPayment(context.consumeData, undefined, true).then((res: ConsumeRequestData) => {
            event.consumeData = res;
          });
          eventPromises.push(metadataPromise);

          let operation: OperationType;
          if(!event.consumeData) {
            if(event.indexed.Operation === OperationType.IN) {
              operation = OperationType.IN;
            } else if(event.indexed.Operation === OperationType.OUT) {
              operation = OperationType.OUT;
            }
            event.consumeData = {Operation: operation};
          }
        }
        if(context && context.place_id) {
          const placePromise = this.places.getPlace(context.place_id).then((place: Place) => {
            event.place = place;
          }).catch(err => {
            console.error(`Failed to get place for event ${index}:`, err);
            event.place = null;
          });
          eventPromises.push(placePromise);
        }
        return Promise.all(eventPromises).then(() => Promise.resolve());
      });

      return Promise.all(allPromises).then(() => {
        this.save(request, next);
        return Promise.resolve();
      }).catch(err => {
        console.error('Error during the process of move events:', err);
        return Promise.reject(err);
      });
    } else {
      return Promise.resolve();
    }
  }

  private hasNotBeenModified(req1: RequestWithDuplicata, req2: RequestWithDuplicata): boolean {
    return req1.updatedOn === req2.updatedOn;
  }

  private calculateUsageCount(request: RequestWithDuplicata): number {
    if(!request?.events || request.events.length === 0) {
      return request.usageCount;
    }

    const sortedEvents = request.events.sort((a, b) => new Date(a.updatedOn).getTime() - new Date(b.updatedOn).getTime());
    const countForcedOutEvents = sortedEvents.reduce((count, event, index) => {
      if(event.indexed.Operation === OperationType.OUT &&
        index > 0 &&
        sortedEvents[index - 1].type === PlatformEventType.PAYMENT_USED_FORCED) {
        return count + 1;
      }
      return count;
    }, 0);
    return countForcedOutEvents + request.usageCount;
  }

  private isUsage(request: RequestWithDuplicata): boolean {
    if(request.document?.isUsage) {
      return eval(request.document.isUsage)({Operation: request.checkoutIsNext ? OperationType.OUT : OperationType.IN});
    } else {
      return false;
    }
  }

  getRequestData(specificPerson?: Person[]): Promise<void> {
    const relevantPersons = specificPerson?.length ? specificPerson : this.api.listPersons;
    return Promise.all(relevantPersons.filter(x => x.id).map(person => this.api.personRequests(person.id, true, !this.api.env.useGuichet))).then(payments => {
      const paymentData = payments.reduce((acc, curVal) => acc.concat(curVal), []);
      paymentData.forEach(payment => {
        this.processOfflineData(payment, false);
        if(payment.internalIndexedData) {
          payment.metadata = {};
          Object.keys(payment?.indexed).forEach(k => {
            payment.metadata[k] = payment.indexed[k];
            payment.metadata[toCamelCase(k)] = payment.indexed[k];
          });
        } else {
          payment.metadata = isJsonParsable(payment?.metadata);
        }
      });
      if(this.request.allMyPayments?.length) {
        paymentData.forEach((newPayment) => {
          const existingPaymentIndex = this.request.allMyPayments.findIndex(payment => payment.id === newPayment.id);
          if(existingPaymentIndex >= 0) {
            this.request.allMyPayments[existingPaymentIndex] = newPayment;
          } else {
            this.request.allMyPayments.push(newPayment);
          }
        });
      } else {
        this.request.allMyPayments = paymentData;
      }
      this.request.allMyPayments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      // get all batchIds for each person
      this.request.getBatchIds(this.api.listPersons);
      this.request.allMyApplications = undefined;
      // get all batchIds for all users
      this.request.allBatchIds = (specificPerson?.length ? this.request.allMyPayments : paymentData)?.reduce((acc: string[], request: RequestWithDuplicata) => {
        if(!acc.includes(request.batchId) && request.batchId && !documentTypeIsVisaExtAB(request.operationId) && !documentTypeIsVisaExtC(request.operationId)) {
          acc.push(request.batchId);
        }
        return acc;
      }, []) || [];
      // get all extensions for all users
      this.request.allExtensions = (specificPerson?.length ? this.request.allMyPayments : paymentData)?.reduce((extensionsAccumulator: RequestWithDuplicata[], currentRequest: RequestWithDuplicata) => {
        const isExtension = documentTypeIsVisaExtAB(currentRequest.operationId) || documentTypeIsVisaExtC(currentRequest.operationId);
        if(isExtension) {
          const existingIndex = extensionsAccumulator.findIndex((existingExtension) => existingExtension.id === currentRequest.id);
          if(existingIndex > -1) {
            if(extensionsAccumulator[existingIndex].updatedOn !== currentRequest.updatedOn) {
              extensionsAccumulator[existingIndex] = currentRequest;
            }
          } else {
            extensionsAccumulator.push(currentRequest);
          }
        }
        return extensionsAccumulator;
      }, this.request.allExtensions || []);
      //get all applications for all users sorted by batchIds, with each their assigned requests and their extensions within those requests
      this.request.allMyApplications = this.request.allBatchIds.map((batchId: string) => ({
        batchId,
        requests: this.request.allMyPayments.filter((request: RequestWithDuplicata) => request.batchId === batchId).map((request: RequestWithDuplicata) => {
          request.extensions = this.request.allExtensions.filter((extension: RequestWithDuplicata) => extension.indexed.VisaReference === request.id);
          return request;
        })
      }));
      this.request.invalidatePersons = [];
    });
  }

  cleanupExistingPayments(doRequestCall = true){
    if(this.cart.currentApplication) {
      this.request.invalidatePersons = this.cart.currentApplication.requests.map(request => request.person);
      if(doRequestCall) {
        this.getRequestData(this.request.invalidatePersons).then(() => this.request.cleanupError(this.cart.currentApplication.isExtend, 'Cleanup existing payments and get new data'));
      } else {
        this.request.cleanupError(this.cart.currentApplication.isExtend, 'Cleanup existing payments');
      }
    }
  }

  /**
   * Fill parent data and ensures moveEvents, metadata, and consumeData are populated.
   * @param current The child request whose parent is to be filled.
   * @returns A Promise resolving to the populated parent RequestWithDuplicata.
   */
  private getParentData(current: RequestWithDuplicata): Promise<RequestWithDuplicata> {
    const parent = current.parent;
    if(!parent) return Promise.resolve(undefined);

    this.processOfflineData(parent, false);
    parent.moveEvents = this.getMoveEvents(parent);
    parent.eventsNotice = this.getEventsNotices(parent);
    parent.eventsNotes = this.getEventsNotes(parent);
    return Promise.all([
      this.getConsumeData(parent, true),
      this.getSerializedData(parent, true)
    ]).then(([consumeData, serializedData]) => {
      parent.consumeData = consumeData;
      parent.metadata = serializedData;
      parent.moveEvents.forEach(event => event.consumeData = consumeData);
      return this.processEvents(parent, false).then(() => parent);
    });
  }
}
